import React, { useContext, useState, useEffect } from "react";
import { ChatContext } from "../context/ChatContext";
import { AuthContext } from "../context/AuthContext";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

const GroupMembers = ({ onClose }) => {
  const { data } = useContext(ChatContext);
  const { currentUser, userEventCode } = useContext(AuthContext);
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGroupData = async () => {
      if (data.isGroupChat && data.chatId !== "null") {
        try {
          const groupDoc = await getDoc(doc(db, "groupChats", data.chatId));
          if (groupDoc.exists()) {
            setGroupData(groupDoc.data());
          }
        } catch (error) {
          console.error("Error fetching group data:", error);
        }
      }
      setLoading(false);
    };

    fetchGroupData();
  }, [data.chatId, data.isGroupChat]);

  const handleRemoveMember = async (memberToRemove) => {
    if (!groupData || memberToRemove.uid === currentUser.uid) return;
    
    try {
      // Check if current user is the group creator
      if (groupData.groupInfo.createdBy !== currentUser.uid) {
        alert("Only the group creator can remove members.");
        return;
      }

      const updatedMembers = groupData.groupInfo.members.filter(
        member => member.uid !== memberToRemove.uid
      );

      // Update group chat document
      await updateDoc(doc(db, "groupChats", data.chatId), {
        "groupInfo.members": updatedMembers
      });

      // Remove from user's chat list (both formats)
      const eventCode = userEventCode || currentUser?.eventCode;
      const memberEventChatId = `${memberToRemove.uid}_${eventCode}`;
      
      try {
        // Try event-specific format first
        await updateDoc(doc(db, "userChats", memberEventChatId), {
          [data.chatId]: null
        });
      } catch (error) {
        // Fallback to old format
        await updateDoc(doc(db, "userChats", memberToRemove.uid), {
          [data.chatId]: null
        });
      }

      // Update all remaining members' userChats with new member list
      for (const member of updatedMembers) {
        const memberChatId = `${member.uid}_${eventCode}`;
        
        try {
          // Try event-specific format first
          await updateDoc(doc(db, "userChats", memberChatId), {
            [data.chatId + ".groupInfo.members"]: updatedMembers
          });
        } catch (error) {
          // Fallback to old format
          await updateDoc(doc(db, "userChats", member.uid), {
            [data.chatId + ".groupInfo.members"]: updatedMembers
          });
        }
      }

      // Update local state
      setGroupData(prev => ({
        ...prev,
        groupInfo: {
          ...prev.groupInfo,
          members: updatedMembers
        }
      }));

    } catch (error) {
      console.error("Error removing member:", error);
      alert("Error removing member. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="groupMembers">
        <div className="groupMembersModal">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (!data.isGroupChat || !groupData) {
    return null;
  }

  const isCreator = groupData.groupInfo.createdBy === currentUser.uid;

  return (
    <div className="groupMembers">
      <div className="groupMembersModal">
        <div className="groupMembersHeader">
          <h3>Group Members</h3>
          <button onClick={onClose} className="closeBtn">×</button>
        </div>
        
        <div className="groupMembersContent">
          <div className="groupInfo">
            <h4>{groupData.groupInfo.name}</h4>
            <p>{groupData.groupInfo.members.length} members</p>
          </div>

          <div className="membersList">
            {groupData.groupInfo.members.map((member) => (
              <div key={member.uid} className="memberItem">
                <img src={member.photoURL} alt="" />
                <div className="memberInfo">
                  <span className="memberName">
                    {member.displayName}
                    {member.uid === currentUser.uid && " (You)"}
                    {member.uid === groupData.groupInfo.createdBy && " (Admin)"}
                  </span>
                </div>
                {isCreator && member.uid !== currentUser.uid && (
                  <button 
                    onClick={() => handleRemoveMember(member)}
                    className="removeMemberBtn"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>

          {isCreator && (
            <div className="adminActions">
              <p className="adminNote">As the group admin, you can remove members from this group.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupMembers;
